<template>
  <div class="ch-page">
    <SnackbarComp :SnackbarComponent="snackbar" />

    <CallIntelligenceDrawer
      v-model="showIntelligenceDrawer"
      :call="selectedCall"
      :organization="organization"
    />

    <div class="ch-panel">
      <div class="ch-panel__toolbar">
        <div class="ch-panel__toolbar-start">
          <span class="ch-panel__count">{{ totalCalls }} call{{ totalCalls === 1 ? '' : 's' }}</span>
          <v-select
            v-model="selectedTaskId"
            :items="campaignOptions"
            item-title="title"
            item-value="value"
            placeholder="All campaigns"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            style="max-width: 260px"
          />
        </div>
        <CallHistoryHeader
          :total-calls="totalCalls"
          :to-number="toNumberDisplay"
          :loading="loading"
          :is-session-ready="isSessionReady"
          :is-search-active="isSearchActive"
          @refresh="handleRefresh"
        />
      </div>

      <CallHistoryList
        :calls="paginatedCalls"
        :loading="loading"
        :is-search-active="isSearchActive"
        @open-detail="openCallDetail"
      />

      <PortalListPagination
        class="ch-panel__footer"
        :page="page"
        :page-count="pageCount"
        :items-per-page="itemsPerPage"
        :range-label="rangeLabel"
        :total="filteredCalls.length"
        @update:page="setPage"
        @update:items-per-page="setItemsPerPage"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import PortalListPagination from '@/components/Extras/PortalListPagination.vue'
import CallIntelligenceDrawer from '@/components/CallHistory/CallIntelligenceDrawer.vue'
import CallHistoryHeader from '@/components/CallHistory/CallHistoryHeader.vue'
import CallHistoryList from '@/components/CallHistory/CallHistoryList.vue'
import {
  useCallHistoryData,
  getCallHistoryErrorMessage,
} from '@/composables/callHistory/useCallHistoryData'
import { fetchCampaigns } from '@/api/campaignsApi'
import { normalizeCampaignsList } from '@/utils/campaigns'
import { unwrapApiList } from '@/utils/graphql'

defineOptions({ name: 'CallHistoryPage' })

const store = useStore()
const snackbar = ref({})
const showIntelligenceDrawer = ref(false)
const selectedCall = ref(null)

const organization = computed(() => store.state.organization)

// KAN-31: campaign filter -- selectedTaskId is a reactive ref passed
// straight into useCallHistoryData, which re-fetches whenever it changes
// (see that composable's own taskId watcher). null/cleared means "every
// campaign", the org-wide behavior this page always had.
const selectedTaskId = ref(null)
const campaigns = ref([])
const campaignOptions = computed(() => [
  { title: 'All campaigns', value: null },
  ...campaigns.value.map((c) => ({ title: c.name || c.campaignId, value: c.campaignId })),
])
onMounted(async () => {
  const response = await fetchCampaigns()
  campaigns.value = normalizeCampaignsList(unwrapApiList(response))
})

const {
  loading,
  page,
  itemsPerPage,
  pageCount,
  isSessionReady,
  filteredCalls,
  paginatedCalls,
  totalCalls,
  toNumberDisplay,
  rangeLabel,
  isSearchActive,
  loadCallHistory,
  setPage,
  setItemsPerPage,
} = useCallHistoryData({ taskId: selectedTaskId })

function showError(payload) {
  snackbar.value = {
    SnackbarVmodel: true,
    SnackbarColor: 'error',
    SnackbarText: getCallHistoryErrorMessage(payload),
  }
}

async function handleRefresh() {
  try {
    const result = await loadCallHistory()
    if (result && result.status === 'ERROR') {
      showError(result)
    }
  } catch (err) {
    showError(err)
  }
}

function openCallDetail(call) {
  if (!call) return
  selectedCall.value = { ...call }
  showIntelligenceDrawer.value = true
}
</script>
